import { HospitalService } from 'src/app/core/services/hospital.service';
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { IdleService } from './../../../../core/services/idle.service';
import { PatientAiService } from './../../../../core/services/patient-ai.service';
import { PatientService } from './../../../../core/services/patient.service';
import { ScreenWakeService } from './../../../../core/services/screen-wake.service';
import { AiDialogComponent } from './../../../../shared/components/ai-dialog/ai-dialog.component';
import { AI_DIALOG_PANEL_CLASSES, FLOATING_AI_DIALOG_BASE } from './../../../../shared/config/floating-ai-dialog.config';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import * as _ from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-patient-list-item',
  templateUrl: './patient-list-item.component.html',
  styleUrls: ['./patient-list-item.component.scss']
})
export class PatientListItemComponent extends BaseComponent {
  @Input() patient: any;
  @Input() isInpatient: boolean = false;

  @ViewChild('inpatientDetailsDialog') inpatientDetailsDialog?: TemplateRef<any>;
  editingPatient: any = {}
  hospitals: any[] = [];
  wards: any[] = [];
  isLoading: boolean = false;

  constructor(
    private patientService: PatientService,
    private idleService: IdleService,
    private patientAIService: PatientAiService,
    private router: Router,
    private screenWake: ScreenWakeService,
    private hospitalService: HospitalService,
    private snack: MatSnackBar,
    fs: FeedbackService,
    md: MatDialog) {
    super(fs, md);

    this.hospitalService.hospitals$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(hospitals => {
        this.hospitals = hospitals;
      });
  }

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }


  navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }


  openAIDialog(event: MouseEvent, patientId: string, groupId: string) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.idleService.overrideIdleTime(180);
    this.screenWake.disableScreenSleep();
    let dialogRef = this.dialog.open(AiDialogComponent, {
      ...FLOATING_AI_DIALOG_BASE,
      //width: '80vw',
      //height: '85vh',
      disableClose: true,
      panelClass: AI_DIALOG_PANEL_CLASSES,
      data: { patientId, groupId, type: 'ward-round' }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        this.idleService.startIdleChecking();
        this.screenWake.enableScreenSleep();
        if (request) {
          this.patientService.getPatientById(patientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
              this.patientAIService.setEditingAIRequest(request);
              this.router.navigate(['/patients', {
                outlets: {
                  patient: ['edit-request', request.id],
                }
              }]);
            }, error => {
              console.error(error);
              this.showToastMessage('There was an error loading the patient file. Please try again.');
            });

        }
        dialogRef.componentInstance.resetAll();
      });
  }

  selectHospital(event: { value: string }) {
    const selectedHospitalId = event.value;
    // Find the hospital object from the cached this.hospitals array
    const selectedHospital = this.hospitals.find(hospital => hospital.id === selectedHospitalId);

    if (selectedHospital && selectedHospital.wards) {
      // Sort wards alphabetically using Lodash
      this.wards = _.orderBy(selectedHospital.wards, ['name'], ['asc']);
    } else {
      // Reset wards if no hospital is found or the hospital has no wards
      this.wards = [];
    }
  }

  openEditPatientDialog(event: MouseEvent, patient: any) {
    event.preventDefault();
    event.stopPropagation();
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.selectHospital({ value: patient.hospitalId });
    setTimeout(() => {
      this.editingPatient = { id: patient.id, hospitalId: patient.hospitalId, wardId: patient.wardId, bed: patient.bed };
      this.dialog.open(this.inpatientDetailsDialog!, { width: '600px' });
    });

  }

  openDischarge() {
    this.patientService.getPatientById(this.editingPatient.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['discharge', this.editingPatient.id],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
    this.dialog.closeAll();
  }

  saveBedDetails() {
    this.isLoading = true;
    this.patientService.updatePatient(this.editingPatient.id, this.editingPatient)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (result) => {
          this.showToastMessage('Patient updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient: ', error);
          this.showToastMessage('There was an error updating the patient. Please try again.');
        }
      });
    this.isLoading = false;
    this.dialog.closeAll();
  }
}